import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import {
  HStack,
  VStack,
} from '@ayble-health/dashboard-components';
import { Input } from '../Form/Input';
import { Loader } from '../Loader';
import { HealthCoachDashboardList } from './HealthCoachDashboardList';
import { FilterSidebar } from './FilterSidebar/FilterSidebar';
import { PanelStats } from './PanelStats';
import { ROW_PADDING_X } from './columns';
import { brand } from '../../theme/chakra';
import { useHealthCoachDashboard } from './hooks/useHealthCoachDashboard';

export const HealthCoachDashboard = () => {
  const filters = useHealthCoachDashboard();
  const { isLoadingUsers, filteredUsers, stats } = filters;

  return (
    // 100% rather than 100vh: this sits below the workspace chrome, so a
    // viewport-height box would hang off the bottom of the screen by exactly
    // that much — and every pixel of it comes out of the list.
    //
    // The zero mins are load-bearing on both axes. This div is a grid item in
    // AppLayout, and grid items default to `min-*: auto` — a content-based
    // floor that outranks `height: 100%`. Without minHeight the div grows to
    // fit the whole patient list, AutoSizer measures that unbounded height and
    // virtualization stops doing anything; without minWidth the same happens
    // sideways, pushing the list past the viewport where it is clipped with no
    // way to scroll to it.
    //
    // No page header: this is a panel-management screen that people keep open
    // all day, so the title, the standing instruction, and the branded nav bar
    // were all costing rows of patients to say things the screen already says.
    <div
      className="health-coach-dashboard"
      style={{ height: '100%', minHeight: 0, minWidth: 0 }}
    >
      {isLoadingUsers ? (
        <Loader loading={isLoadingUsers} />
      ) : (
        // No padding on this row — the filter panel butts against the side nav
        // and the workspace toolbar, so its fill reads as a wall of the layout.
        // The list column carries the page's breathing room instead.
        <Flex h="100%" minH={0} minW={0}>
          <FilterSidebar filters={filters} />

          {/* No horizontal padding here: the gutter lives on the rows
              (ROW_PADDING_X) so the header's divider can span the frame edge
              to edge while the cells stay inset. */}
          <VStack flex={1} minW={0} minH={0} pt={4} pb={4} gap={0} align="stretch">
            {/* Search and panel stats share one band above the list: search
                left, indented to sit flush with the Name column below it, and
                the counts right. Centered against each other so the search
                box reads as level with the stats rather than floating. */}
            <HStack
              justifyContent="space-between"
              alignItems="center"
              gap={8}
              px={ROW_PADDING_X}
              mb={8}
              flexShrink={0}
            >
              {/* `Input`'s own `maxWidth` prop only caps growth — it doesn't
                  make the box grow in the first place, since neither this
                  HStack nor `Input`'s internal `Container` give it a
                  flex-basis. Fixing the width here, on a non-flex wrapper,
                  is what actually makes the box render at that size. */}
              <Box width="400px" flexShrink={0}>
                <Input
                  key={filters.resetToken}
                  // Cloud rather than the default evergreen: search sits
                  // beside the list, where cloud is already the resting
                  // border on the row rules and the header divider.
                  borderColor={brand.cloud}
                  height={38}
                  isClearable
                  maxWidth="400px"
                  onChange={filters.onSearchChange}
                  placeholder="Search Name, ID, Email..."
                  type="text"
                  value={filters.search}
                />
              </Box>

              <PanelStats
                stats={[
                  { label: 'My Total Panel', value: stats.totalPanel },
                  { label: 'New This Week', value: stats.newThisWeek },
                ]}
              />
            </HStack>

            <Box flex={1} minW={0} minH={0}>
              <HealthCoachDashboardList
                users={filteredUsers}
                isPanelEmpty={filters.isPanelEmpty}
                // Applied, not draft: the table has to agree with the filter
                // that produced it, so a pill clicked but not yet applied
                // must not change how rows render.
                appliedVisitTiming={filters.appliedVisitTiming}
              />
            </Box>
          </VStack>
        </Flex>
      )}

      <Outlet />
    </div>
  );
};
